import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Header from "../components/Header";
import StoryBar from "../components/StoryBar";
import AngelCard from "../components/AngelCard";
import { Search, Heart } from "lucide-react";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

const COMUNAS = [
  "Las Condes", "Providencia", "Vitacura", "Ñuñoa", "Lo Barnechea",
  "Santiago Centro", "La Reina", "Maipú", "Viña del Mar", "Concepción"
];
const SERVICIOS = ["Masajes", "Videollamadas", "Escort", "Pack contenido virtual"];

const SectionTitle = ({ kicker, title }) => (
  <div className="flex items-center justify-center gap-3 mb-8 mt-6">
    <div className="hidden sm:block w-12 h-px bg-[#6B6A63]/30" />
    <div className="text-center">
      <div className="text-xs tracking-[0.3em] uppercase text-[#6B6A63]">{kicker}</div>
      <h2 className="font-serif text-3xl sm:text-4xl text-[#1A2118] mt-1">{title}</h2>
    </div>
    <div className="hidden sm:block w-12 h-px bg-[#6B6A63]/30" />
  </div>
);

const TierBlock = ({ kicker, title, accent, angels, favIds, onToggleFav }) => {
  if (!angels.length) return null;
  return (
    <section className="py-10">
      <SectionTitle kicker={kicker} title={title} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        {angels.map((a) => (
          <AngelCard
            key={a.angel_id}
            angel={a}
            favorited={favIds.has(a.angel_id)}
            onToggleFav={onToggleFav}
          />
        ))}
      </div>
    </section>
  );
};

const Home = () => {
  const { user } = useAuth();
  const [angels, setAngels] = useState([]);
  const [topLiked, setTopLiked] = useState([]);
  const [favIds, setFavIds] = useState(new Set());
  const [q, setQ] = useState("");
  const [comuna, setComuna] = useState("");
  const [service, setService] = useState("");

  const load = async () => {
    const params = {};
    if (comuna) params.comuna = comuna;
    if (service) params.service = service;
    const r = await api.get("/angels", { params });
    setAngels(r.data);
    try {
      const t = await api.get("/angels/top-liked", { params: { days: 7, limit: 8 } });
      setTopLiked(t.data || []);
    } catch (e) {
      // top-liked is a nice-to-have; failing is not fatal
      if (process.env.NODE_ENV !== "production") console.debug("top-liked failed", e);
    }
    if (user) {
      try {
        const f = await api.get("/me/favorites");
        setFavIds(new Set(f.data.map((x) => x.angel_id)));
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.debug("favorites failed", e);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [comuna, service, user]);

  const filtered = useMemo(() => {
    if (!q) return angels;
    const qq = q.toLowerCase();
    return angels.filter(
      (a) =>
        a.artistic_name?.toLowerCase().includes(qq) ||
        a.comuna?.toLowerCase().includes(qq)
    );
  }, [angels, q]);

  const byTier = useMemo(() => {
    const groups = { premium: [], gold: [], standard: [] };
    filtered.forEach((a) => {
      if (groups[a.tier]) groups[a.tier].push(a);
    });
    return groups;
  }, [filtered]);

  const toggleFav = async (angel) => {
    if (!user) {
      toast("Inicia sesión para guardar favoritas");
      return;
    }
    try {
      if (favIds.has(angel.angel_id)) {
        await api.delete(`/me/favorites/${angel.angel_id}`);
        setFavIds((s) => { const n = new Set(s); n.delete(angel.angel_id); return n; });
      } else {
        await api.post(`/me/favorites/${angel.angel_id}`);
        setFavIds((s) => new Set(s).add(angel.angel_id));
        toast.success("Añadido a favoritas");
      }
    } catch (e) { toast.error("No se pudo actualizar"); }
  };

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-2 text-center">
        <div className="text-xs tracking-[0.35em] uppercase text-[#6B6A63] mb-3">Directorio Exclusivo</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A2118] font-light">
          El secreto mejor guardado
        </h1>
        <p className="mt-4 text-[#6B6A63] max-w-xl mx-auto">
          Una selección curada de modelos premium. Discreción, elegancia y experiencia.
        </p>

        {/* Search */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto bg-white rounded-2xl p-3 border border-[#E5E2D9] shadow-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6A63]" size={18} strokeWidth={1.5} />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nombre o comuna…"
              className="pl-9 bg-[#FAF9F6] border-[#E5E2D9] rounded-xl"
            />
          </div>
          <Select value={comuna || "_"} onValueChange={(v) => setComuna(v === "_" ? "" : v)}>
            <SelectTrigger data-testid="select-comuna" className="bg-[#FAF9F6] border-[#E5E2D9] rounded-xl">
              <SelectValue placeholder="Comuna" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_">Todas las comunas</SelectItem>
              {COMUNAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={service || "_"} onValueChange={(v) => setService(v === "_" ? "" : v)}>
            <SelectTrigger data-testid="select-service" className="bg-[#FAF9F6] border-[#E5E2D9] rounded-xl">
              <SelectValue placeholder="Servicio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_">Todos los servicios</SelectItem>
              {SERVICIOS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </section>

      <StoryBar />

      {topLiked.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#B8943A]">Ranking semanal</div>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#1A2118] mt-1 flex items-center gap-2">
                <span>Más gustadas</span>
                <span className="text-[#D4AF37]">✦</span>
              </h2>
            </div>
            <div className="text-xs text-[#6B6A63]">Últimos 7 días</div>
          </div>
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-3 -mx-4 px-4">
            {topLiked.map((a, i) => (
              <Link
                key={a.angel_id}
                to={`/angel/${a.angel_id}`}
                data-testid={`top-liked-${a.angel_id}`}
                className={`shrink-0 w-40 sm:w-44 rounded-xl overflow-hidden bg-[#1A2118] angel-card ${a.tier === 'premium' ? 'tier-premium' : a.tier === 'gold' ? 'tier-gold' : 'tier-standard'}`}
              >
                <div className="relative aspect-[9/16]">
                  {a.main_photo_path ? (
                    <img
                      src={fileUrl(a.main_photo_path)}
                      alt={a.artistic_name}
                      className="w-full h-full object-cover no-save"
                      loading="lazy"
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  ) : null}
                  <div className="cherub-watermark absolute inset-0" />
                  <div className="absolute top-2 left-2 h-7 min-w-[28px] px-2 rounded-full bg-black/60 text-white text-xs font-serif flex items-center justify-center backdrop-blur-sm">
                    #{i + 1}
                  </div>
                  <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-[#D4AF37]/95 text-[#1A2118] text-[11px] font-medium">
                    <Heart size={12} strokeWidth={2} className="fill-current" /> {a.likes}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 pt-10 pb-2 px-2 bg-gradient-to-t from-black/85 via-black/45 to-transparent text-center text-white card-text-shadow">
                    <div className="font-serif text-base truncate">{a.artistic_name}</div>
                    <div className="text-[11px] text-white/80 truncate">{a.comuna || "—"}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <TierBlock
          kicker="The Top Tier"
          title="Premium Angels"
          accent="gold"
          angels={byTier.premium}
          favIds={favIds}
          onToggleFav={toggleFav}
        />
        <TierBlock
          kicker="Selección Distinguida"
          title="Gold Angels"
          accent="silver"
          angels={byTier.gold}
          favIds={favIds}
          onToggleFav={toggleFav}
        />
        <TierBlock
          kicker="El Catálogo"
          title="Standard Angels"
          accent="neutral"
          angels={byTier.standard}
          favIds={favIds}
          onToggleFav={toggleFav}
        />

        {filtered.length === 0 && (
          <div className="text-center py-24">
            <div className="font-serif text-2xl text-[#1A2118]">Aún no hay perfiles publicados</div>
            <div className="text-sm text-[#6B6A63] mt-2">Pronto verás aquí nuestra selección.</div>
          </div>
        )}
      </main>

      <footer className="border-t border-[#E5E2D9] mt-10 py-10 text-center text-xs text-[#6B6A63]">
        <div className="tracking-[0.2em] uppercase">© Secret Angels · Discreción · Elegancia · Exclusividad</div>
        <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-2">
          <Link data-testid="footer-privacy" to="/privacidad" className="hover:text-[#1A2118] underline-offset-4 hover:underline">Política de Privacidad</Link>
          <Link data-testid="footer-terms" to="/terminos" className="hover:text-[#1A2118] underline-offset-4 hover:underline">Términos y Condiciones</Link>
          <span className="text-[#A8A9AD]">Solo mayores de 18 años</span>
        </div>
      </footer>
    </div>
  );
};

export default Home;
